/**
 * The cadence briefs' words (Foundry Intelligence, "Update 2026-09-26 (evening)" in
 * docs/plans/2026-09-26_foundry-intelligence.md): a shift, day, week, month or year brief, in the Plant Brief's plain
 * foundry language. A short period holds few castings, so a signal says "watch this" and a risk says "check this":
 * nothing here says what made a rate move. The mechanisms and their standing come from the long-run analysis behind
 * the overall brief. A number the brief states as a fact is printed in the fact's words, looked up by the composer's
 * key (brief_cadence.go, cadenceFacts), so the page and the number check agree; without one, it is formatted as the
 * composer formats it. Pure; no em or en dashes (house style).
 */
import { formatDay, isPlantDay } from "@/lib/plant-days";
import { shownRate } from "./rate-words";
import { shownFor } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import type {
  BriefViewCadence,
  BriefDataState,
  BriefFact,
  BriefLimits,
  BriefSignal,
  BriefSpan,
  CadenceAction,
  CadenceBrief,
  CadenceChildItem,
  CadenceDataAsk,
  CadenceExcursion,
  CadenceLineRow,
  CadenceMover,
  CadenceParetoRow,
  CadenceRisk,
  CadenceScrap,
} from "@/lib/api/brief";
import { count, factTextOf, inline, pct, periodLabel, rateText, SHARE_UNIT, shareOfDayText } from "./words";
import { lotDateWords } from "./lot-dates";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const CADENCE_NOUN: Record<BriefViewCadence, string> = {
  shift: "shift",
  day: "day",
  week: "week",
  month: "month",
  year: "year",
  span: "window",
};

/** A title or a check inside a sentence: an opening "A" or "An" lowered too, an abbreviation kept ("CE per ladle"). */
function inSentence(s: string): string {
  return /^(A|An) /.test(s) ? s[0].toLowerCase() + s.slice(1) : inline(s);
}

/** "17 to 23 Aug 2026", "28 Aug to 3 Sep 2026", "28 Dec 2025 to 3 Jan 2026", "18 Aug 2026". */
export function dayRangeText(first: string, last: string): string {
  if (first === last) return formatDay(first);
  const [fy, fm, fd] = [first.slice(0, 4), Number(first.slice(5, 7)), Number(first.slice(8, 10))];
  if (fy !== last.slice(0, 4)) return `${formatDay(first)} to ${formatDay(last)}`;
  if (fm === Number(last.slice(5, 7))) return `${fd} to ${formatDay(last)}`;
  return `${fd} ${MONTHS[fm - 1]} to ${formatDay(last)}`;
}

/** A shift's key: the plant day, a dot, then the shift calendar's name ("2026-08-18.1"). */
export function parseShiftKey(key: string): { day: string; shift: string } | null {
  const m = /^(\d{4}-\d{2}-\d{2})\.([A-Za-z0-9_-]+)$/.exec(key);
  return m && isPlantDay(m[1]) ? { day: m[1], shift: m[2] } : null;
}

/** A span's key: its first and last plant days ("2026-08-01..2026-08-31"), the window someone chose. */
export function parseSpanKey(key: string): { first: string; last: string } | null {
  const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(key);
  return m && isPlantDay(m[1]) && isPlantDay(m[2]) && m[1] <= m[2] ? { first: m[1], last: m[2] } : null;
}

/** A week's key: the ISO week of the plant day, zero-padded ("2026-W34"). */
export function parseWeekKey(key: string): { year: string; week: number } | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(key);
  const week = m ? Number(m[2]) : 0;
  return m && week >= 1 && week <= 53 ? { year: m[1], week } : null;
}

/** A period as the list and the brief both name it; the list gives its plant days, and so does a brief's window. */
export interface BriefPeriodRef {
  cadence: BriefViewCadence;
  period_key: string;
  first_day?: string;
  last_day?: string;
}

/**
 * The period in plain words: "Shift 1 of 18 Aug 2026", "18 Aug 2026", "Week 34, 17 to 23 Aug 2026", "Aug 2026",
 * "2026". A week's days come from the brief (the plant week may start on any day), and a key that does not read is
 * shown as it is.
 */
export function windowLabel(p: BriefPeriodRef | CadenceBrief): string {
  const key = p.period_key;
  const first = "window" in p ? p.window.first_day : p.first_day;
  const last = "window" in p ? p.window.last_day : p.last_day;
  switch (p.cadence) {
    case "shift": {
      const s = parseShiftKey(key);
      return s ? `Shift ${s.shift.replace(/_/g, " ")} of ${formatDay(s.day)}` : key;
    }
    case "day":
      return isPlantDay(key) ? formatDay(key) : key;
    case "week": {
      const w = parseWeekKey(key);
      if (!w) return key;
      return isPlantDay(first) && isPlantDay(last) ? `Week ${w.week}, ${dayRangeText(first, last)}` : `Week ${w.week} of ${w.year}`;
    }
    case "month":
      return /^\d{4}-(0[1-9]|1[0-2])$/.test(key) ? periodLabel(key) : key;
    case "year":
      return key;
    case "span": {
      const s = parseSpanKey(key);
      return s ? dayRangeText(s.first, s.last) : key;
    }
  }
}

/**
 * The stretch a period is compared with: "the 28 working days before", "the same shift over the 14 working days
 * before"; a window's "the 28 days just before", calendar days (as many as the window holds, and at least 28); a
 * pinned baseline (phase II) is "the stretch the plant marked stable".
 */
export function baselinePhrase(cadence: BriefViewCadence, periods: number, kind?: BriefSpan["kind"], days?: number): string {
  if (cadence === "span" && kind !== "pinned") {
    // Calendar days: "plant days" read as the days the plant cast, which a day's baseline counts (P124).
    return days === undefined ? "the days just before" : `the ${count(days)} days just before`;
  }
  if (kind === "pinned") {
    const pinned = "the stretch the plant marked stable";
    return cadence === "shift" ? `the same shift over ${pinned}` : pinned;
  }
  // A day's baseline counts the days the plant cast, not the calendar's (the 2026-09-28 walk, D4: "the 22 days before
  // (30 Jul to 26 Aug)" spans 28): working days.
  if (cadence === "shift") {
    return periods === 1
      ? "the same shift the working day before"
      : `the same shift over the ${count(periods)} working days before`;
  }
  const noun = cadence === "day" ? "working day" : CADENCE_NOUN[cadence];
  return periods === 1 ? `the ${noun} before` : `the ${count(periods)} ${noun}s before`;
}

type Compared = Pick<CadenceBrief, "cadence" | "baseline">;

/** A brief's own figures: a number it states as a fact is printed in the fact's words (factTextOf). */
type WithFacts = { facts?: readonly BriefFact[] };

/** A brief's data state, where a sentence reads whether its period is partial (cadence/v13). */
type WithData = { data?: Partial<BriefDataState> };

/** A stretch's plant days, both ends included; undefined where its days do not read. */
export function spanDaysOf(s: Pick<BriefSpan, "first_day" | "last_day">): number | undefined {
  const a = Date.parse(`${s.first_day}T00:00:00Z`);
  const z = Date.parse(`${s.last_day}T00:00:00Z`);
  return Number.isFinite(a) && Number.isFinite(z) && z >= a ? Math.round((z - a) / 86_400_000) + 1 : undefined;
}

/** The phrase for a brief's own baseline: a window's by its baseline's own days. */
export function comparedPhrase(cadence: BriefViewCadence, baseline: BriefSpan): string {
  return baselinePhrase(cadence, baseline.periods, baseline.kind, cadence === "span" ? spanDaysOf(baseline) : undefined);
}

export function baselineLabel(b: Compared): string {
  return b.baseline ? `compared with ${comparedPhrase(b.cadence, b.baseline)}` : "with nothing to compare it with yet";
}

/** The brief's opening line: what the period is compared with, and the baseline's own days. */
export function headerLine(b: Compared): string {
  if (!b.baseline) {
    if (b.cadence === "span") return "There is nothing to compare this window with yet.";
    // A shift holds no scrap, so it has no baseline to set it against: its excursions are read against the plant's
    // usual (the 2026-09-28 walk, S2: "nothing to compare this shift with yet" on every shift).
    if (b.cadence === "shift") return "Its process conditions are read against the plant's usual.";
    const n = CADENCE_NOUN[b.cadence];
    return `There is nothing to compare this ${n} with yet: the ${n}s before it build its baseline.`;
  }
  const s = baselineLabel(b);
  return `${s[0].toUpperCase()}${s.slice(1)} (${dayRangeText(b.baseline.first_day, b.baseline.last_day)}).`;
}

export const SIGNAL_WORDS: Record<BriefSignal, string> = {
  above: "above its limits",
  below: "below its limits",
  within: "within its limits",
  none: "no limits yet",
};

/**
 * Limits as wide as a rate can be: their upper end at 100% (cadence/v7 clamps it there; a brief composed before read
 * past it, far past 100% on a day that cast a handful of pieces). The period cast too few for them to judge (the 2026-09-28
 * page walk, P107).
 */
export function limitsTooWide(l: BriefLimits | null | undefined): boolean {
  return !!l && l.high >= 1;
}

/** Said of a rate whose limits are too wide to judge by (limitsTooWide). */
export const TOO_FEW_TO_JUDGE = "too few castings for its limits to judge";

/** A rate's signal in words: "within its limits", or too few castings to judge where its limits say nothing. */
export function signalWordsOf(r: { signal: BriefSignal; limits?: BriefLimits | null }): string {
  return limitsTooWide(r.limits) ? "too few castings to judge" : SIGNAL_WORDS[r.signal];
}

/** A rate's limits in words, each end its fact's words under key (key.limits.low, key.limits.high) where stated. */
function limitsOf(l: BriefLimits, key: string, t: (key: string, fallback: string) => string): string {
  return `${t(`${key}.limits.low`, pct(l.low))} to ${t(`${key}.limits.high`, pct(l.high))}`;
}

/**
 * " against 2.9% over the 28 days before"; a shift's is "in the same shift over ...". Empty without a baseline. The
 * rate is its fact's words (key.baseline_rate) where stated.
 */
function against(rate: number | null, b: Compared, key: string, t: (key: string, fallback: string) => string): string {
  if (rate === null || !b.baseline) return "";
  const over = b.cadence === "shift" ? "in" : "over";
  return ` against ${t(`${key}.baseline_rate`, pct(rate))} ${over} ${comparedPhrase(b.cadence, b.baseline)}`;
}

/**
 * Where the period has no outcomes: a shift no scrap was booked on (a plant that books its scrap per production day
 * books none on its shifts; one that books its lots per shift gives a shift its own scrap, cadence/v10).
 */
export function noScrapSentence(cadence: BriefViewCadence): string {
  return cadence === "shift"
    ? "No scrap is booked on this shift (a plant that books its scrap per production day books none on its shifts), so its brief is about process risk: the excursions and the castings poured under them."
    : "No scrap is booked for this period.";
}

/**
 * "Scrap ran 6.4% of 3,400 castings, above its limits (1.2% to 4.8%) against 2.9% over the 28 days before." Each
 * figure is its fact's words (scrap.rate, scrap.cast, scrap.limits.*, scrap.baseline_rate) where the brief states it.
 */
export function scrapSentence(
  b: Pick<CadenceBrief, "cadence" | "baseline" | "scrap"> & Partial<Pick<CadenceBrief, "lot_dates">> & WithFacts & WithData,
): string {
  const s = b.scrap;
  if (!s) return noScrapSentence(b.cadence);
  if (s.rate === null) return "No castings with a known outcome were booked in this period.";
  const t = factTextOf(b.facts);
  const wide = limitsTooWide(s.limits);
  const limits = s.signal !== "none" && s.limits && !wide ? `, ${SIGNAL_WORDS[s.signal]} (${limitsOf(s.limits, "scrap", t)})` : "";
  const base = against(s.baseline_rate, b, "scrap", t);
  // A partial period's verdict says it is partial before it (cadence/v13).
  // Where the plant dates its lots by the inspection or the shipment, the period's castings are those inspected (or
  // booked at shipping) in it (B1b T3); the pour's is what a period's castings are said to be without a word.
  const dated = b.lot_dates ? ` ${lotDateWords(b.lot_dates).dated} ${thisPeriod(b.cadence)}` : "";
  let out = ledBy(
    partialLead(b.data),
    `Scrap ran ${t("scrap.rate", pct(s.rate))} of ${t("scrap.cast", count(s.cast))} castings${dated}${limits}${base}`,
  );
  if (wide) return `${out}; ${TOO_FEW_TO_JUDGE}.${targetClause(s, "scrap", t)}`;
  // A window someone chose has limits from the days just before it (Laney p' on those days), none when too few of
  // them cast anything: that is why, never a "yet", since a window is not composed again on its own.
  if (!limits && base) {
    out += b.cadence === "span" ? "; too few days before it cast anything to set limits" : `; there are ${SIGNAL_WORDS.none}`;
  }
  if (!limits && !base) out += "; there is nothing to compare it with yet";
  return `${out}.${targetClause(s, "scrap", t)}`;
}

/**
 * The scrap's verdict beside its figures: within, above or below its limits, against the periods before it, and the
 * plant's target, with no number the tiles and the band beside it state (the 2026-09-28 walk, D1: the rate, the limits
 * and the baseline were each written three times). scrapSentence says it all in one, where there are no tiles.
 */
export function scrapVerdict(b: Pick<CadenceBrief, "cadence" | "baseline" | "scrap"> & WithFacts & WithData): string {
  const s = b.scrap;
  if (!s || s.rate === null) return scrapSentence(b);
  const t = factTextOf(b.facts);
  if (limitsTooWide(s.limits)) {
    const vs = s.baseline_rate === null || !b.baseline ? "" : `; it ran ${s.rate > s.baseline_rate ? "above" : s.rate < s.baseline_rate ? "below" : "level with"} ${comparedPhrase(b.cadence, b.baseline)}`;
    return `This ${CADENCE_NOUN[b.cadence]} cast too few for its limits to judge its scrap${vs}.${targetClause(s, "scrap", t)}`;
  }
  const said = s.signal !== "none" && s.limits ? `${SIGNAL_WORDS[s.signal]}` : "no limits yet";
  const base = s.baseline_rate;
  const against =
    base === null || !b.baseline
      ? ""
      : `, ${s.rate > base ? "above" : s.rate < base ? "below" : "level with"} ${comparedPhrase(b.cadence, b.baseline)}`;
  return `${ledBy(partialLead(b.data), `Scrap ran ${said}${against}`)}.${targetClause(s, "scrap", t)}`;
}

/**
 * " It is above the plant's target of 5.0%." after a rate the plant set a target for (cadence/v5): above it, or within
 * it (at or below). The target is its fact's words (key.target) where stated. Empty without a target.
 */
function targetClause(
  r: Pick<CadenceScrap, "target" | "target_signal">,
  key: string,
  t: (key: string, fallback: string) => string,
): string {
  if (r.target === undefined || !r.target_signal) return "";
  return ` It is ${r.target_signal === "above" ? "above" : "within"} the plant's target of ${t(`${key}.target`, pct(r.target))}.`;
}

/**
 * A defect's rate against the plant's target: "above the plant's target of 3.0%", or "within ..."; "" without a target.
 * The target is its fact's words (pareto.<defect>.target) where stated.
 */
export function paretoTargetText(r: CadenceParetoRow, facts?: readonly BriefFact[]): string {
  if (r.target === undefined || !r.target_signal) return "";
  const t = factTextOf(facts);
  return `${r.target_signal === "above" ? "above" : "within"} the plant's target of ${t(`pareto.${r.defect}.target`, pct(r.target))}`;
}

/**
 * The facts' key of a mover's figures: a defect's are its Pareto row's (pareto.<defect>), a part's its own
 * (mover.part.<part>), as the composer states them.
 */
function moverFactKey(m: Pick<CadenceMover, "kind" | "key">): string {
  if (m.kind === "line") return `line.${m.key}`;
  return m.kind === "part" ? `mover.part.${m.key}` : `pareto.${m.key}`;
}

/** A line's label as a name: "B" is "Line B", a label that says line is kept ("Moulding line 2"). */
export function lineName(label: string): string {
  return /\bline\b/i.test(label) ? label : `Line ${label}`;
}

/** The periods a line's own limits are drawn from: a window's are its days before, any other cadence's its periods. */
function lineBaselineNoun(cadence: BriefViewCadence | undefined): string {
  return cadence === "span" || !cadence ? "days" : `${CADENCE_NOUN[cadence]}s`;
}

/**
 * "Line B: 8.0% of 1,000 castings, above its limits (3.1% to 6.9%) against 5.0% over the 28 working days before.": a
 * line's rate against its own limits (cadence/v13). Each figure is its fact's words (line.<line>.*) where stated. A
 * line with too few periods of its own before it has none: "too few days on this line before it for limits of its own".
 */
export function lineSentence(l: CadenceLineRow, b: Compared & WithFacts & WithData): string {
  const t = factTextOf(b.facts);
  const key = `line.${l.line}`;
  const rate = l.rate === null ? rateText(null) : t(`${key}.rate`, pct(l.rate));
  const cast = t(`${key}.cast`, count(l.cast));
  const wide = limitsTooWide(l.limits);
  const limits = l.limits && l.signal !== "none" && !wide ? `, ${SIGNAL_WORDS[l.signal]} (${limitsOf(l.limits, key, t)})` : "";
  // A line with too few periods of its own before it has no limits (it is not held to the plant's pooled ones): say so.
  const none = l.limits ? SIGNAL_WORDS.none : `too few ${lineBaselineNoun(b.cadence)} on this line before it for limits of its own`;
  const judged = wide ? `, ${TOO_FEW_TO_JUDGE}` : !limits ? `, ${none}` : "";
  return `${ledBy(partialLead(b.data), `${lineName(l.line)}: ${rate} of ${cast} castings${limits}${judged}${against(l.baseline_rate, b, key, t)}`, true)}.`;
}

/**
 * A defect's share of the period's castings, or a part's own scrap rate, against its limits and baseline. A sentence
 * starts with a capital, a defect's word too ("Cold lap was ..."), except after a partial period's lead, where
 * the plant's word is kept as it writes it. A rate under its limits is fewer than usual, said so. Limits that read
 * "0.0% to 0.0%" say nothing a person can use: the defect was next to absent before, which the sentence says instead,
 * and that it is above its limits, never how far (the 2026-10-24 sweep, item 6.4: it may be one piece or thousands). A rate above nothing that rounds to nothing reads
 * "under 0.01%" (shownRate, item 7.2).
 */
export function moverSentence(m: CadenceMover, b: Compared & WithFacts & WithData): string {
  const t = factTextOf(b.facts);
  const key = moverFactKey(m);
  const rate = m.rate === null ? rateText(null) : shownRate(t(`${key}.rate`, pct(m.rate)), m.rate);
  const pieces = `${t(`${key}.pieces`, count(m.pieces))} ${m.pieces === 1 ? "piece" : "pieces"}`;
  const lead = partialLead(b.data);
  // A defect's label is the plant's word: a capital at the sentence's start, kept as written after a partial lead.
  const defectWord = lead ? m.label : capitalOf(m.label);
  const head =
    m.kind === "part"
      ? `Part ${m.label} scrapped ${rate} of its castings (${pieces})`
      : m.kind === "line"
        ? `${lineName(m.label)} scrapped ${rate} of its castings (${pieces})`
        : `${defectWord} was ${rate} of the castings (${pieces})`;
  // "Part" and "Line" are lowered after a partial period's lead; a defect's word is kept.
  const said = ledBy(lead, head, m.kind === "defect");
  if (m.signal === "above" && m.limits && limitsReadNothing(m.limits, key, t) && b.baseline) {
    const base = m.baseline_rate === null ? "" : ` (${shownRate(t(`${key}.baseline_rate`, pct(m.baseline_rate)), m.baseline_rate)})`;
    return `${said}. It was next to absent over ${comparedPhrase(b.cadence, b.baseline)}${base}, so it is ${SIGNAL_WORDS.above}.`;
  }
  const limits = m.limits ? ` (${limitsOf(m.limits, key, t)})` : "";
  const fewer = m.signal === "below" ? "fewer than usual, " : "";
  return `${said}, ${fewer}${SIGNAL_WORDS[m.signal]}${limits}${against(m.baseline_rate, b, key, t)}.`;
}

/** Limits whose both ends read "0.0%": the rate was next to absent over the baseline, so they mark nothing out. */
function limitsReadNothing(l: BriefLimits, key: string, t: (key: string, fallback: string) => string): boolean {
  return t(`${key}.limits.low`, pct(l.low)) === "0.0%" && t(`${key}.limits.high`, pct(l.high)) === "0.0%";
}

const WINDOW_WORD: Record<CadenceExcursion["source"], string> = {
  prior_window: "its window",
  plant_window: "the plant's window",
  grade_aim: "the grade's aim",
  control_limits: "its control limits",
};
const WAY_WORD: Record<CadenceExcursion["direction"], string> = { low: "below", high: "above", both: "outside" };
const WORST_WORD: Record<CadenceExcursion["direction"], string> = {
  low: "the lowest",
  high: "the highest",
  both: "the furthest out",
};

/** Which way an excursion left its window: below, above, or outside it. */
export function excursionWay(e: Pick<CadenceExcursion, "direction">): string {
  return WAY_WORD[e.direction];
}

/**
 * The window an excursion left: its window, the plant's window (for a grade, where it is one grade's), the grade's aim,
 * or its control limits. A window with one end is its floor or its ceiling: the plant's, where the plant set it.
 */
export function excursionWindow(e: Pick<CadenceExcursion, "source" | "grade" | "bound" | "direction">): string {
  const grade = e.grade ? ` for grade ${e.grade}` : "";
  if (e.bound !== undefined) {
    const end = e.direction === "high" ? "ceiling" : "floor";
    return `${e.source === "plant_window" ? "the plant's" : "its"} ${end}${grade}`;
  }
  return `${WINDOW_WORD[e.source]}${grade}`;
}

/**
 * What an excursion counts, in its count_unit: a reading, a ladle, or a heat (a condition the plant set a window on and
 * records once per heat: PR5). A document composed before the unit existed counts readings where it carries minutes
 * and ladles where it does not.
 */
export function excursionThing(e: Pick<CadenceExcursion, "count_unit" | "minutes">): "reading" | "ladle" | "heat" {
  if (e.count_unit === "ladles") return "ladle";
  if (e.count_unit === "heats") return "heat";
  if (e.count_unit === "readings") return "reading";
  return e.minutes === undefined ? "ladle" : "reading";
}

/**
 * A condition's value as the composer states it (brief_cadence.go, cadenceValue): four significant figures, then its
 * unit after a space when it has one ("1452 °C", "0.026 %").
 */
export function conditionValueText(v: number, unit: string, prefs?: DisplayPrefs): string {
  // A tracking share is a share of the day (brief/v19): "41.2%", never "0.412 fraction".
  if (unit === SHARE_UNIT) return shareOfDayText(v);
  // The value is stored in `unit`; an org whose choice converts it reads the converted value and its unit. With no
  // choice, or one that does not convert this unit, exactly the stored value and the unit as written.
  const s = shownFor(unit, prefs);
  const text = String(Number(s.convert(v).toPrecision(4)));
  return s.unit ? `${text} ${s.unit}` : text;
}

/** A duration as the composer states it (cadenceMinutes): whole minutes ("95 min"), a tenth when not whole. */
export function minutesText(minutes: number): string {
  const r = Math.floor(minutes * 10 + 0.5 + 1e-9) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)} min`;
}

/**
 * "Residual magnesium was below its window (0.03 % to 0.04 %) in 6 of 14 ladles; the lowest was 0.026 %." The count is
 * in its count_unit, readings, ladles or heats; a document composed before the unit existed counts readings where it carries
 * minutes and ladles where it does not. Given its place in the brief, each number is its fact's words (excursion.<i>.count, .of,
 * .minutes, .worst, .band.low, .band.high) where the brief states it.
 */
export function excursionSentence(
  e: CadenceExcursion,
  index?: number,
  facts?: readonly BriefFact[],
  prefs?: DisplayPrefs,
): string {
  const lookup = factTextOf(facts);
  const t = (name: string, fallback: string) => (index === undefined ? fallback : lookup(`excursion.${index}.${name}`, fallback));
  const unit = e.condition.unit;
  const thing = excursionThing(e);
  const band = e.band
    ? ` (${t("band.low", conditionValueText(e.band[0], unit, prefs))} to ${t("band.high", conditionValueText(e.band[1], unit, prefs))})`
    : e.bound !== undefined
      ? ` (${t("bound", conditionValueText(e.bound, unit, prefs))})`
      : "";
  const outOf = `${t("count", count(e.count))} of ${t("of", count(e.of))} ${e.of === 1 ? thing : `${thing}s`}`;
  const time = e.minutes === undefined ? "" : `, ${t("minutes", minutesText(e.minutes))} in all`;
  const worst = e.worst === undefined ? "" : `; ${WORST_WORD[e.direction]} was ${t("worst", conditionValueText(e.worst, unit, prefs))}`;
  return `${e.condition.label} was ${excursionWay(e)} ${excursionWindow(e)}${band} in ${outOf}${time}${worst}.`;
}

/**
 * How an excursion's share outside compares with the plant's usual (cadence/v4): "As usual for this plant: 31.3% of
 * ladles over the 28 days before." Only one above its usual is news and carries risks and actions; one as usual is
 * background. The usual share is its fact's words (excursion.<i>.baseline_share) where stated. Empty in a brief
 * composed before it. With no limits to judge by (signal "none": too few periods before it, or a usual share that does
 * not vary) but a usual share, the share is said and no verdict: "Over the 12 weeks before, 37.5% of ladles were outside
 * it. Those weeks are not enough yet to say whether this is more than usual." "There is no usual" is said only where
 * there is none (the 2026-10-24 sweep, item 6.1).
 */
export function excursionUsualSentence(
  e: CadenceExcursion,
  index?: number,
  facts?: readonly BriefFact[],
  b?: Compared,
): string {
  if (!e.signal) return "";
  if (e.baseline_share === undefined) return "There is no usual to compare it with yet.";
  const lookup = factTextOf(facts);
  const t = (name: string, fallback: string) => (index === undefined ? fallback : lookup(`excursion.${index}.${name}`, fallback));
  // A shift's usual is its plant day's baseline (the days before it), never its own same-shift baseline.
  const phrase = b?.baseline && b.cadence !== "shift" ? comparedPhrase(b.cadence, b.baseline) : "the days before";
  if (e.signal === "none") {
    const periods = b?.cadence && b.cadence !== "shift" && b.cadence !== "span" ? `${CADENCE_NOUN[b.cadence]}s` : "days";
    return (
      `Over ${phrase}, ${t("baseline_share", pct(e.baseline_share))} of ${excursionThing(e)}s were outside it. ` +
      `Those ${periods} are not enough yet to say whether this is more than usual.`
    );
  }
  if (e.signal === "above" && e.baseline_share === 0) return `More than usual: it had not left ${excursionWindow(e)} in ${phrase}.`;
  const lead = e.signal === "above" ? "More than usual" : e.signal === "below" ? "Fewer than usual" : "As usual for this plant";
  return `${lead}: ${t("baseline_share", pct(e.baseline_share))} of ${excursionThing(e)}s over ${phrase}.`;
}

/** An excursion's short name, for a link: "Residual magnesium below its window". */
export function excursionTitle(e: CadenceExcursion): string {
  return `${e.condition.label} ${excursionWay(e)} ${excursionWindow(e)}`;
}

/** "A, B, C, D, E and 2 more". */
function shortList(items: string[], most = 5): string {
  if (items.length <= most) return items.join(", ");
  return `${items.slice(0, most).join(", ")} and ${items.length - most} more`;
}

/**
 * "Castings of parts P-1042, P-2210 from the shift's 5 heats may have been poured under it: check <what to check>."
 * A risk names the parts of the whole period (the composer's parts made), not only those poured while the condition
 * was out, so it says "may have been". Where its excursion counts heats, the heats are those outside the window ("from
 * the 9 heats outside the plant's window", the 2026-10-24 sweep, item 6.2: never every heat of the day), the count its
 * fact's words (excursion.<i>.count); else the period's heats, the fact risks.heats's words where stated.
 */
export function riskSentence(
  r: CadenceRisk,
  b?: { cadence?: BriefViewCadence; facts?: readonly BriefFact[]; excursions?: readonly CadenceExcursion[] },
): string {
  const parts = r.parts.length === 0 ? "" : ` of ${r.parts.length === 1 ? "part" : "parts"} ${shortList(r.parts)}`;
  const t = factTextOf(b?.facts);
  const check = r.check.length > 0 ? `: check ${r.check.map(inSentence).join("; ")}` : "";
  const e = b?.excursions?.[r.excursion];
  if (e && excursionThing(e) === "heat" && e.count > 0) {
    const out = t(`excursion.${r.excursion}.count`, count(e.count));
    const heats = `${out} ${e.count === 1 ? "heat" : "heats"} ${excursionWay(e)} ${excursionWindow(e)}`;
    return `Castings${parts} from the ${heats} may have been poured under it${check}.`;
  }
  const n = t("risks.heats", count(r.heats));
  const whose = b?.cadence ? `the ${CADENCE_NOUN[b.cadence]}'s ` : "";
  const heats = r.heats > 0 ? ` from ${whose}${n} ${r.heats === 1 ? "heat" : "heats"}` : "";
  return `Castings${parts}${heats} may have been poured under it${check}.`;
}

/** "A and B", "A, B and C". */
function andList(items: string[]): string {
  return items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** What a period's data asks are ranked by (cadence/v9), and where the whole list is. */
export function dataAsksLead(cadence: BriefViewCadence): string {
  return `Ranked by the scrap each would let the plant explain ${thisPeriod(cadence)}; the overall brief lists them all.`;
}

/**
 * A period's data ask (cadence/v9): what it would let the plant test, in the plant's words, and the period's scrapped
 * pieces at stake, in the words of its fact ask.<n>.pieces (n the ask's place in the list from 1).
 */
export function cadenceAskSentence(
  a: CadenceDataAsk,
  index: number,
  cadence: BriefViewCadence,
  facts?: readonly BriefFact[],
): string {
  const what = andList(a.defects.map((d) => d.label));
  const stake = factTextOf(facts)(`ask.${index + 1}.pieces`, count(a.pieces));
  // A sentence, as the overall brief's (the 2026-10-24 sweep, item 8.4).
  const which = a.defects.length > 1 ? "which together scrapped" : "which scrapped";
  return `${a.ask}: would let the plant test ${what}, ${which} ${stake} pieces ${thisPeriod(cadence)}.`;
}

/** "this day", "this month", "these days" (a span). */
function thisPeriod(cadence: BriefViewCadence | undefined): string {
  return cadence === "span" ? "these days" : `this ${CADENCE_NOUN[cadence ?? "day"]}`;
}

const capitalOf = (s: string) => (s ? `${s[0].toUpperCase()}${s.slice(1)}` : s);

/**
 * A shift's label as a name in a sentence: "1" is "shift 1", "Night" is "Night shift", a label that says shift is kept
 * ("Day shift").
 */
export function shiftName(label: string): string {
  const l = label.trim();
  if (/shift/i.test(l)) return l;
  return /^[0-9A-Z]{1,3}$/.test(l) ? `shift ${l}` : `${l} shift`;
}

/** Shift labels as names in a list: "shift 1", "shifts 2 and 3", "Night shift and shift 2". */
function shiftNames(labels: readonly string[]): string {
  if (labels.length > 1 && labels.every((l) => /^[0-9A-Z]{1,3}$/.test(l.trim()))) {
    return `shifts ${andList(labels.map((l) => l.trim()))}`;
  }
  return andList(labels.map(shiftName));
}

/** A partial period's data: the shifts that usually book and had not booked its latest day (cadence/v13). */
type PartialData = Pick<BriefDataState, "partial_day" | "missing_shifts" | "booked_shifts">;

/** Whether a period's latest day is partial: a shift that usually books had not booked it. */
export function isPartial(d: PartialData | undefined): boolean {
  return (d?.missing_shifts?.length ?? 0) > 0;
}

/**
 * "Shift 2 has not booked yet, so the day is partial: its figures are of shift 1 alone, and this brief will be revised
 * when it does." A longer period names the day ("has not booked 28 Aug 2026 yet, so the week is partial").
 */
function partialSentence(d: PartialData, cadence?: BriefViewCadence): string {
  const missing = d.missing_shifts ?? [];
  const many = missing.length > 1;
  const who = capitalOf(shiftNames(missing));
  const booked = d.booked_shifts ?? [];
  const of = booked.length > 0 ? `${shiftNames(booked)} alone` : "the lots booked so far";
  const day = cadence && cadence !== "day" && d.partial_day ? ` ${formatDay(d.partial_day)}` : "";
  const noun = cadence ? CADENCE_NOUN[cadence] : "day";
  const figures = day ? "that day's figures" : "its figures";
  return `${who} ${many ? "have" : "has"} not booked${day} yet, so the ${noun} is partial: ${figures} are of ${of}, and this brief will be revised when ${many ? "they do" : "it does"}.`;
}

/**
 * "So far, with shift 2 still to book, " before a partial period's verdict on a rate against its limits, and before
 * its gap to the best stretch (gap-words), so no figure of a partial day reads as the whole day's. Empty on a whole
 * period.
 */
export function partialLead(d: PartialData | undefined): string {
  if (!d || !isPartial(d)) return "";
  return `So far, with ${shiftNames(d.missing_shifts ?? [])} still to book, `;
}

/** A sentence led by partialLead: its first word lowered after the lead, a defect's label kept as the plant writes it. */
export function ledBy(lead: string, sentence: string, keepCase = false): string {
  if (!lead) return sentence;
  return `${lead}${keepCase ? sentence : sentence[0].toLowerCase() + sentence.slice(1)}`;
}

/** Whether the period's data is all in, and which streams stop short of its end. */
export function dataStateSentence(d: BriefDataState, cadence?: BriefViewCadence): string {
  // A shift that usually books had not booked the period's latest day (cadence/v13): the period is partial, said
  // first; any late stream after it.
  if (isPartial(d)) {
    const partial = partialSentence(d, cadence);
    if (d.late.length === 0) return partial;
    return `${partial} ${andList(d.late)} also ${d.late.length === 1 ? "stops" : "stop"} before the period ends.`;
  }
  // No heat and no reading in it (cadence/v7): said beside what its scrap's own state is, never instead of it (P104).
  if (d.process === "none") {
    const none = `no process data covers ${thisPeriod(cadence)}`;
    if (d.complete) return cadence === "shift" ? `${capitalOf(none)}.` : `Its scrap is all in, but ${none}.`;
    return `${capitalOf(none)}. ${lateSentence(d, cadence)}`;
  }
  if (d.complete) return "All of the period's data is in.";
  return lateSentence(d, cadence);
}

/** Which streams stop short of the period's end, and what then becomes of its brief. */
function lateSentence(d: BriefDataState, cadence?: BriefViewCadence): string {
  // A span is composed when someone asks for it, never again on its own: what it lacks waits for the next ask. The
  // streams that stop before its end are named, as a period's are.
  if (cadence === "span") {
    if (d.late.length === 0) {
      return "Its last days have not closed yet, so this brief reads only the days that have: ask for it again once they close.";
    }
    const one = d.late.length === 1;
    return `The data does not reach the window's end: ${andList(d.late)} ${one ? "stops" : "stop"} before it. Ask for it again once ${one ? "it has" : "they have"} caught up.`;
  }
  const revised = "so this brief will be revised when it arrives.";
  if (d.late.length === 0) return `The data is not all in yet, ${revised}`;
  return `The data is not all in: ${andList(d.late)} ${d.late.length === 1 ? "stops" : "stop"} before the period ends, ${revised}`;
}

/** Which lots an import holds as booked: their outcome cannot be read as a rate. */
const HELD_RULE = "the import holds a lot whose scrap is more than its castings, falls on a day with none, or is negative.";

/**
 * A span's casting lots held as booked, not counted in its scrap: "3 casting lots were held as booked, with 1,250
 * scrapped, ...". Each figure is its fact's words (held.lots, held.scrap_pieces) where stated. Empty with none held.
 */
export function heldSentence(b: Pick<CadenceBrief, "held"> & WithFacts): string {
  const h = b.held;
  if (!h || h.lots <= 0) return "";
  const t = factTextOf(b.facts);
  const n = t("held.lots", count(h.lots));
  const pieces = t("held.scrap_pieces", count(h.scrap_pieces));
  return h.lots === 1
    ? `${n} casting lot was held as booked, with ${pieces} scrapped, and is not counted above: ${HELD_RULE} The plant can check it in its own records.`
    : `${n} casting lots were held as booked, with ${pieces} scrapped, and are not counted above: ${HELD_RULE} The plant can check them in its own records.`;
}

const WRITTEN: Record<BriefViewCadence, string> = {
  shift: "about 30 minutes after each shift ends",
  day: "when the production day closes, before the morning meeting",
  week: "when the plant week closes",
  month: "when the month closes",
  year: "when the year closes",
  span: "when someone asks for it",
};

/** When a cadence's brief is written (the plan's levels); a window's, when someone asks for it. */
export function writtenSentence(cadence: BriefViewCadence): string {
  return cadence === "span"
    ? `A window's brief is written ${WRITTEN.span}.`
    : `A ${CADENCE_NOUN[cadence]} brief is written ${WRITTEN[cadence]}.`;
}

/**
 * A notable item of a period below: a mover against its limits, or an excursion against its window. A mover's kind
 * (childMoverKind: the item does not carry it) names what moved: "Part P-1042 scrapped above its limits.", "Line B
 * scrapped above its limits.", a defect by its word. Without a kind, the item's label as it is. The first word is a
 * capital (the 2026-10-24 sweep, items 6.3 and 6.4).
 */
export function childItemSentence(c: CadenceChildItem, kind?: CadenceMover["kind"]): string {
  if (c.kind === "excursion") {
    const way = c.signal === "above" ? "above" : c.signal === "below" ? "below" : "outside";
    return `${capitalOf(c.label)} was ${way} its window.`;
  }
  const where = c.signal && c.signal !== "none" ? SIGNAL_WORDS[c.signal] : "outside its limits";
  if (kind === "part") return `Part ${c.label} scrapped ${where}.`;
  if (kind === "line") return `${lineName(c.label)} scrapped ${where}.`;
  return `${capitalOf(c.label)} was ${where}.`;
}

/**
 * What moved, when nothing did: nothing left its limits, or there are no limits yet to leave. On a partial period the
 * verdict says so first ("So far, with shift 2 still to book, nothing moved outside its limits.").
 */
export function nothingMovedSentence(b: Pick<CadenceBrief, "cadence" | "scrap"> & WithData): string {
  if (limitsTooWide(b.scrap?.limits)) return `${capitalOf(thisPeriod(b.cadence))} cast too few for any limits to judge what moved.`;
  if (b.scrap && b.scrap.limits === null) {
    if (b.cadence === "span") {
      return "There are no limits to move outside: too few days before this window cast anything to set them.";
    }
    return `There are no limits yet to move outside: too few ${CADENCE_NOUN[b.cadence]}s before this one to set them.`;
  }
  return ledBy(partialLead(b.data), "Nothing moved outside its limits.");
}

export function noExcursionsSentence(cadence: BriefViewCadence, data?: Pick<BriefDataState, "process">): string {
  if (data?.process === "none") {
    return `No process data covers ${thisPeriod(cadence)}, so no condition could be read against its window.`;
  }
  if (cadence === "span") return "No process condition left its window on these days.";
  return `No process condition left its window this ${CADENCE_NOUN[cadence]}.`;
}

/**
 * Why a shift or a day lists no risk: no condition left its window; or those that did ran as they usually do (only news
 * carries a risk: above the plant's usual, or with no usual yet); or none the playbook ties to a defect. Never "no
 * castings were poured while a condition was outside its window" when one was (the 2026-09-28 walk).
 */
export function noRisksSentence(
  excursions: readonly Pick<CadenceExcursion, "signal">[] = [],
  data?: Pick<BriefDataState, "process">,
): string {
  if (excursions.length === 0 && data?.process === "none") {
    return "No process data covers it, so no castings could be put at risk.";
  }
  if (excursions.length === 0) return "No condition ran outside its window, so no castings are at risk.";
  if (excursions.some((e) => e.signal === "above" || e.signal === "none")) {
    return "The conditions that ran outside their windows are none the playbook ties to a defect, so no castings are flagged at risk.";
  }
  return "The conditions outside their windows ran as they usually do for this plant, so no castings are flagged at risk.";
}

/**
 * Under the movers of a period no process data covers (P105: July said Sonic's residual Mg link was "borne out" with
 * no July process data): each mechanism's standing is the overall analysis's, untested in the period itself.
 */
export function moversStandingNote(b: { cadence: BriefViewCadence; data: Pick<BriefDataState, "process"> }): string | null {
  if (b.data.process !== "none") return null;
  return `Each mechanism's standing below is the overall analysis's: ${thisPeriod(b.cadence)} holds no process data to test it in.`;
}

export function noActionsSentence(cadence: BriefViewCadence): string {
  if (cadence === "span") return "Nothing to act on in these days.";
  return `Nothing to act on this ${CADENCE_NOUN[cadence]}.`;
}

/** The periods each cadence rolls up; a shift has none below it. */
const BELOW: Record<BriefViewCadence, string> = {
  shift: "",
  day: "shifts",
  week: "days",
  month: "days",
  year: "months",
  span: "days",
};

export function childrenLead(cadence: BriefViewCadence): string {
  return `The notable items of its ${BELOW[cadence]}, as their own briefs found them.`;
}

export function noChildrenSentence(cadence: BriefViewCadence): string {
  return BELOW[cadence] ? `Nothing notable in its ${BELOW[cadence]}.` : "";
}

/** Which defect an action is for, its mechanism where known, and why it is on the list. */
export function actionNote(
  a: CadenceAction,
  labelOf: (defect: string) => string,
  titleOf?: (mechanismId: string) => string | undefined,
  excursions?: readonly CadenceExcursion[],
): string {
  const title = titleOf?.(a.mechanism_id);
  const what = title ? `${labelOf(a.defect)} (${inSentence(title)})` : labelOf(a.defect);
  if (a.source === "excursion") return `For ${what}, put at risk by a process excursion.`;
  const why =
    a.source === "target"
      ? `For ${what}, above the plant's target.`
      : a.source === "defect"
        ? `For ${what}, which the period booked.`
        : `For ${what}, which moved outside its limits.`;
  // The mechanism leads because its condition ran unusual in this period: say which reading.
  const e = a.excursion === undefined ? undefined : excursions?.[a.excursion];
  if (!e) return why;
  return `${why} Its ${inSentence(e.condition.label)} ran ${excursionWay(e)} ${excursionWindow(e)} more than usual in this period.`;
}

/**
 * The actions' groups, in the order the brief lists them, each with its heading: cadence/v5's three, then every other
 * defect the period booked (cadence/v6: any defect is scrap).
 */
export const ACTION_GROUPS: readonly { source: CadenceAction["source"]; title: string }[] = [
  { source: "target", title: "Above the plant's targets" },
  { source: "mover", title: "Moved outside their limits" },
  { source: "excursion", title: "Process excursions" },
  { source: "defect", title: "The period's other defects" },
];

/** What the "What to do" section says it lists, in its groups' order. */
export const ACTIONS_LEAD =
  "From the playbook: first what runs above the plant's targets, then what moved outside its limits, then the process excursions, then every other defect the period booked.";

/** A defect's actions under one mechanism, for one reason: said once, its actions listed under it. */
export interface ActionBucket {
  /** Unique among the buckets. */
  key: string;
  first: CadenceAction;
  texts: string[];
  /** The bucket's actions, one per text in its order: each its own recommendation, by its key. */
  actions: CadenceAction[];
}

/**
 * The actions in buckets: those in a row for the same defect, mechanism, reason and excursion share one note, so the
 * page says "For Cold Iron (...), which moved outside its limits." once over its three actions, not once under each.
 * The brief lists a mechanism's actions together, so a bucket never splits. Pure.
 */
export function actionBuckets(actions: readonly CadenceAction[]): ActionBucket[] {
  const out: ActionBucket[] = [];
  let previous = "";
  for (const a of actions) {
    const same = [a.source, a.defect, a.mechanism_id, a.excursion ?? ""].join("|");
    const last = out[out.length - 1];
    if (last && same === previous) {
      last.texts.push(a.text);
      last.actions.push(a);
    } else {
      out.push({ key: `${out.length}|${same}`, first: a, texts: [a.text], actions: [a] });
    }
    previous = same;
  }
  return out;
}
